import {
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import useAppContextProvider from "../../AppContext/useAppContextProvider";
import {
  AttachTagsToMiners,
  DetachTagsFromMiners,
} from "../../api/api";

type TagResult = {
  status: string;
  Msg?: string;
  errorCode?: string;
};

type DetachTagsPayload = {
  minernames: string[];
  tags: string[];
};

export const usePostDetachTagsFromMiners = () => {
  const queryClient = useQueryClient();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const { getAccessTokenSilently, isAuthenticated } =
    useAuth0();
  const [authToken, setAuthToken] = useState<string | null>(
    null
  );

  const mutation = useMutation({
    mutationFn: ({
      minernames,
      tags,
      token,
    }: DetachTagsPayload & {
      token: string;
    }): Promise<TagResult> =>
      DetachTagsFromMiners({ minernames, tags }, token),

    onSuccess: (data) => {
      if (data.status === "error") {
        setToastMessage(
          `Error code: ${data.errorCode}, Error msg: ${data.Msg}` ||
            "Error adding tag"
        );
      } else {
        setToastMessage("Tags detached successfully");
      }
      setToastOpen(true);
    },

    onError: (error) => {
      console.error("Error detaching tag Tag:", error);
      setToastMessage("Error detaching Tags from miners");
      setToastOpen(true);
    },
  });

  const mutateWithToken = useCallback(
    async (
      { tags, minernames }: DetachTagsPayload,
      options?: {
        onSuccess?: (data: TagResult) => void;
        onError?: (error: unknown) => void;
        onSettled?: () => void;
      }
    ) => {
      const token = await getAccessTokenSilently();

      mutation.mutate(
        {
          tags,
          minernames,
          token,
        },
        {
          onSuccess: options?.onSuccess,
          onError: options?.onError,
          onSettled: options?.onSettled,
        }
      );
    },
    [getAccessTokenSilently, mutation]
  );

  return { ...mutation, mutate: mutateWithToken };
};
